/**
 * THE MATCH DRAFT'S SHAPES: the firm administrator's saved answers to the move
 * matcher, one per legacy client, kept between sittings (decision 041; spec
 * docs/specs/bms/2026-10-24-001-move-matcher-keeps-progress.md §3, §7.1, cut
 * to its v1). Stored in `insurance_broker_bms.org_migration_match_choices`.
 *
 * BROWSER-SAFE: `effect` only. The view group's contract references these
 * schemas and the settings slice imports the contract by path. A contract
 * cannot name the matcher's barrel, so the three words are spelled here; the
 * matcher's `DraftChoice` (`matcher/domain/draft-states.ts`) holds the same
 * three, and a unit test keeps the two equal.
 *
 * NOTHING MOVES FROM THE DRAFT. It only fills the agreement page's answers;
 * the administrator still agrees once over the resolution the page builds.
 *
 * Ids, closed words, a hash and times only: no name or value the firm typed.
 */
import { Schema as S } from "effect"

export const MATCH_DRAFT_CHOICES = ["join", "apart", "setAside"] as const
export const MatchDraftChoice = S.Literal(...MATCH_DRAFT_CHOICES).annotations({ identifier: "CutoverMatchDraftChoice" })
export type MatchDraftChoice = typeof MatchDraftChoice.Type

/** 32 lower-case hex characters: the matcher's `rowFingerprintOf` of the row as the screen showed it. */
export const SeenFingerprint = S.String.pipe(S.pattern(/^[0-9a-f]{32}$/)).annotations({ identifier: "CutoverSeenFingerprint" })

/** One saved answer. */
export const MatchDraftRow = S.Struct({
  id: S.UUID,
  legacyId: S.UUID,
  choice: MatchDraftChoice,
  /** The Cova CRM client a `join` names; null for `apart` and `setAside`. */
  bookId: S.NullOr(S.UUID),
  seenFingerprint: S.String,
  /** The administrator's Clerk id. */
  decidedByUserId: S.String,
  /** ISO 8601. */
  decidedAt: S.String,
}).annotations({ identifier: "CutoverMatchDraftRow" })
export type MatchDraftRow = typeof MatchDraftRow.Type

/** The firm's whole draft. A few hundred rows per firm; no page. */
export const MatchDraft = S.Struct({
  rows: S.Array(MatchDraftRow),
}).annotations({ identifier: "CutoverMatchDraft" })
export type MatchDraft = typeof MatchDraft.Type

/** What the page sends when the administrator answers a match. `bookId` is required for `join` and refused otherwise. */
export const SaveMatchChoicePayload = S.Struct({
  choice: MatchDraftChoice,
  bookId: S.NullOr(S.UUID),
  seenFingerprint: SeenFingerprint,
}).annotations({ identifier: "CutoverSaveMatchChoicePayload" })
export type SaveMatchChoicePayload = typeof SaveMatchChoicePayload.Type

export const ClearedMatchChoice = S.Struct({ legacyId: S.UUID }).annotations({ identifier: "CutoverClearedMatchChoice" })
export type ClearedMatchChoice = typeof ClearedMatchChoice.Type

/** What a draft write says while the firm cannot take an agreement. */
export const DRAFT_CLOSED_MESSAGE =
  "This firm cannot take an agreement now: it already has one, or its move has started, so its matches cannot be changed."
